import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  Link,
  SpaceBetween,
  StatusIndicator,
  Table,
  type TableProps,
} from '@cloudscape-design/components';
import { fetchTenants, type Tenant } from '../api/tenants';
import { fetchDeployments, type RoleDeployment } from '../api/deployments';
import { fetchVersions } from '../api/stack-versions';
import { runPostDeploy, type PostDeployResult } from '../api/post-deploy';
import { CONFIG } from '../config';

interface DeployedInstance {
  tenantId: string;
  tenantName: string;
  companyName: string;
  customerAccountId: string;
  stackName: string;
  version: string;
  frontendUrl: string;
  customDomain: string;
  apiUrl: string;
  cognitoPoolId: string;
  cognitoClientId: string;
  cloudFrontId: string;
  mcpEndpoint: string;
  pcLinkStatus: string;
  postDeployStatus: string;
  postDeploySteps: Record<string, string>;
  deployedAt: string;
  hostingMode: string;
  /** Deployment lifecycle status (integrated, deployed, failed, validation_failed, pending, …). */
  deploymentStatus: string;
}

async function fetchPC3Instances(): Promise<DeployedInstance[]> {
  const tenantsRes = await fetchTenants();
  const instances: DeployedInstance[] = [];

  for (const tenant of tenantsRes.tenants) {
    try {
      const depRes = await fetchDeployments(tenant.tenantId);
      // Show every PC3 deployment, not just healthy ones. Filtering to
      // integrated/deployed previously hid failed and in-progress instances,
      // so a broken deployment was invisible here even though it showed up
      // in Customer Deployments. Operators need to see and act on those.
      const pc3Deployments = depRes.deployments.filter((d) => d.productId === 'aeos-pc3' && d.status !== 'removed');

      for (const pc3 of pc3Deployments) {
        const o = pc3.stackOutputs || {};
        instances.push({
          tenantId: tenant.tenantId,
          tenantName: tenant.name,
          companyName: tenant.companyName,
          customerAccountId: o.customerAccountId || '',
          stackName: o.stackName || '',
          version: o.version || o.lastRoleUpdateVersion || '',
          frontendUrl: o.FrontendUrl || '',
          customDomain: pc3.customDomain || '',
          apiUrl: o.ApiUrl || '',
          cognitoPoolId: o.UserPoolId || '',
          cognitoClientId: o.UserPoolClientId || '',
          cloudFrontId: o.CloudFrontDistributionId || '',
          mcpEndpoint: o.MCPEndpoint || '',
          pcLinkStatus: o.pcLinkStatus || 'unknown',
          postDeployStatus: pc3.postDeployStatus || 'not_started',
          postDeploySteps: pc3.postDeploySteps || {},
          deployedAt: o.deployedAt || o.checkedAt || pc3.updatedAt,
          hostingMode: o.hostingMode || 'self-deployed',
          deploymentStatus: pc3.status,
        });
      }
    } catch { /* skip tenants without deployments */ }
  }

  return instances;
}


const STEP_ORDER = ['read_outputs', 'deploy_frontend', 'create_admin', 'seed_migration', 'pc_probe'];
const STEP_LABELS: Record<string, string> = {
  read_outputs: 'Read Outputs',
  deploy_frontend: 'Deploy Frontend',
  create_admin: 'Create Admin',
  seed_migration: 'Seed Migration',
  pc_probe: 'PC Link Probe',
};

function stepIcon(val: string): { type: 'success' | 'error' | 'in-progress' | 'pending'; label: string } {
  if (!val) return { type: 'pending', label: 'Pending' };
  if (val.startsWith('completed')) return { type: 'success', label: val };
  if (val.startsWith('failed') || val.startsWith('skipped')) return { type: 'error', label: val };
  if (val.startsWith('in_progress')) return { type: 'in-progress', label: 'In progress' };
  return { type: 'pending', label: val || 'Pending' };
}

/** Stable per-row key — a tenant can have more than one PC3 deployment row. */
function instanceKey(i: DeployedInstance): string {
  return `${i.tenantId}::${i.stackName}::${i.deploymentStatus}`;
}

export function PC3Console() {
  const [searchParams] = useSearchParams();
  const preselectedTenant = searchParams.get('tenant') || '';
  const [instances, setInstances] = useState<DeployedInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInstance, setSelectedInstance] = useState<DeployedInstance | null>(null);
  const [stableVersion, setStableVersion] = useState<string>('');

  const [runningPostDeploy, setRunningPostDeploy] = useState<string | null>(null);
  const [postDeployResult, setPostDeployResult] = useState<PostDeployResult | null>(null);

  // Fetch the current stable version for upgrade indicators
  useEffect(() => {
    fetchVersions('aeos-pc3')
      .then((res) => {
        const current = res.versions.find((v) => v.status === 'current');
        if (current) setStableVersion(current.version);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchPC3Instances()
      .then((res) => {
        setInstances(res);
        if (preselectedTenant) {
          const match = res.find((i) => i.tenantId === preselectedTenant);
          if (match) setSelectedInstance(match);
        }
      })
      .finally(() => setLoading(false));
  }, [preselectedTenant]);

  async function handleRunPostDeploy(instance: DeployedInstance): Promise<void> {
    setRunningPostDeploy(instance.tenantId);
    setPostDeployResult(null);
    try {
      const result = await runPostDeploy({
        tenantId: instance.tenantId,
        customerAccountId: instance.customerAccountId,
        version: instance.version || '0.4.0',
        stackName: instance.stackName,
        contactEmail: 'admin@pc3.gdna.io',
      });
      setPostDeployResult(result);
      fetchPC3Instances().then(setInstances);
    } catch (e) {
      setPostDeployResult({ status: 'failed', error: e instanceof Error ? e.message : 'unknown', results: {} });
    } finally {
      setRunningPostDeploy(null);
    }
  }

  async function handleRunStep(instance: DeployedInstance, step: string): Promise<void> {
    setRunningPostDeploy(instance.tenantId);
    try {
      const result = await runPostDeploy({
        tenantId: instance.tenantId,
        customerAccountId: instance.customerAccountId,
        version: instance.version || '0.4.0',
        stackName: instance.stackName,
        contactEmail: 'admin@pc3.gdna.io',
        step,
      });
      setPostDeployResult(result);
      fetchPC3Instances().then(setInstances);
    } catch (e) {
      setPostDeployResult({ status: 'failed', error: e instanceof Error ? e.message : 'unknown', results: {} });
    } finally {
      setRunningPostDeploy(null);
    }
  }

  // ── KPI calculations ──
  const totalInstances = instances.length;
  const behindCount = stableVersion ? instances.filter((i) => i.version && i.version !== stableVersion).length : 0;
  const postDeployIncomplete = instances.filter((i) => i.postDeployStatus !== 'completed').length;
  const pcLinked = instances.filter((i) => i.pcLinkStatus === 'linked').length;
  const saasHosted = instances.filter((i) => i.hostingMode === 'saas-hosted').length;


  // ── Table columns ──
  const columns: TableProps.ColumnDefinition<DeployedInstance>[] = [
    {
      id: 'tenant', header: 'Tenant', width: 160,
      cell: (item) => (
        <SpaceBetween size="xxxs">
          <span style={{ fontWeight: 600 }}>{item.tenantName}</span>
          <Box fontSize="body-s" color="text-body-secondary">{item.companyName}</Box>
        </SpaceBetween>
      ),
    },
    {
      id: 'deploymentStatus', header: 'Deployment', width: 130,
      cell: (item) => {
        const map: Record<string, 'success' | 'error' | 'in-progress' | 'pending' | 'stopped'> = {
          integrated: 'success', deployed: 'success',
          parameters_set: 'in-progress', cfn_launched: 'in-progress', pending: 'pending',
          validation_failed: 'error', failed: 'error', removed: 'stopped',
        };
        return (
          <StatusIndicator type={map[item.deploymentStatus] || 'pending'}>
            {item.deploymentStatus.replace(/_/g, ' ')}
          </StatusIndicator>
        );
      },
    },
    {
      id: 'site', header: 'Site URL', width: 260,
      cell: (item) => {
        const custom = item.customDomain;
        const cf = item.frontendUrl;
        if (!custom && !cf) return <StatusIndicator type="warning">No URL</StatusIndicator>;
        return (
          <SpaceBetween size="xxxs">
            {custom && <Link external href={custom} fontSize="body-s">{custom.replace('https://', '')}</Link>}
            {cf && <Link external href={cf} fontSize="body-s">{cf.replace('https://', '')}</Link>}
          </SpaceBetween>
        );
      },
    },
    {
      id: 'hosting', header: 'Hosting', width: 100,
      cell: (item) => item.hostingMode === 'saas-hosted'
        ? <Box fontSize="body-s">☁️ SaaS</Box>
        : <Box fontSize="body-s">🏗️ Self</Box>,
    },
    {
      id: 'account', header: 'Account', width: 120,
      cell: (item) => item.customerAccountId
        ? <code style={{ fontSize: 12 }}>{item.customerAccountId}</code>
        : <Box color="text-status-inactive">—</Box>,
    },
    {
      id: 'version', header: 'Version', width: 130,
      cell: (item) => {
        if (!item.version) return <Box color="text-status-inactive">—</Box>;
        const isCurrent = stableVersion && item.version === stableVersion;
        const isBehind = stableVersion && item.version !== stableVersion;
        return (
          <SpaceBetween direction="horizontal" size="xxs">
            <code style={{ fontSize: 12, fontWeight: 600 }}>{item.version}</code>
            {isCurrent && <StatusIndicator type="success">stable</StatusIndicator>}
            {isBehind && <Box fontSize="body-s" color="text-status-warning">⬆ {stableVersion}</Box>}
          </SpaceBetween>
        );
      },
    },
    {
      id: 'postDeploy', header: 'Post-Deploy', width: 130,
      cell: (item) => {
        const map: Record<string, 'success' | 'error' | 'in-progress' | 'pending'> = {
          completed: 'success', failed: 'error', in_progress: 'in-progress',
        };
        return (
          <StatusIndicator type={map[item.postDeployStatus] || 'pending'}>
            {item.postDeployStatus === 'not_started' ? 'Not started' : item.postDeployStatus}
          </StatusIndicator>
        );
      },
    },
    {
      id: 'pcLink', header: 'PC Link', width: 100,
      cell: (item) => (
        <StatusIndicator type={item.pcLinkStatus === 'linked' ? 'success' : item.pcLinkStatus === 'not_linked' ? 'warning' : 'info'}>
          {item.pcLinkStatus === 'linked' ? 'Linked' : item.pcLinkStatus === 'not_linked' ? 'Not linked' : 'Unknown'}
        </StatusIndicator>
      ),
    },
    {
      id: 'actions', header: '', width: 70,
      cell: (item) => (
        <Link fontSize="body-s" onFollow={(e) => { e.preventDefault(); setSelectedInstance(selectedInstance && instanceKey(selectedInstance) === instanceKey(item) ? null : item); }}>
          {selectedInstance && instanceKey(selectedInstance) === instanceKey(item) ? 'Close' : 'Details'}
        </Link>
      ),
    },
  ];


  return (
    <SpaceBetween size="l">
      <Header variant="h1" description="Deployed PC3 instances — versions, post-deploy status, site URLs, and customer configuration."
        actions={
          <Button iconName="refresh" loading={loading} onClick={() => { setLoading(true); fetchPC3Instances().then(setInstances).finally(() => setLoading(false)); }}>
            Refresh
          </Button>
        }
      >PC3 Console</Header>

      {/* KPI bar */}
      <Container>
        <ColumnLayout columns={5} variant="text-grid">
          <SpaceBetween size="xxxs">
            <Box variant="awsui-key-label">Instances</Box>
            <Box variant="awsui-value-large">{totalInstances}</Box>
          </SpaceBetween>
          <SpaceBetween size="xxxs">
            <Box variant="awsui-key-label">Stable Version</Box>
            <Box variant="awsui-value-large">{stableVersion || '—'}</Box>
          </SpaceBetween>
          <SpaceBetween size="xxxs">
            <Box variant="awsui-key-label">Behind Stable</Box>
            <Box variant="awsui-value-large" color={behindCount > 0 ? 'text-status-warning' : undefined}>{behindCount}</Box>
          </SpaceBetween>
          <SpaceBetween size="xxxs">
            <Box variant="awsui-key-label">Post-Deploy Pending</Box>
            <Box variant="awsui-value-large" color={postDeployIncomplete > 0 ? 'text-status-error' : undefined}>{postDeployIncomplete}</Box>
          </SpaceBetween>
          <SpaceBetween size="xxxs">
            <Box variant="awsui-key-label">PC Linked</Box>
            <Box variant="awsui-value-large">{pcLinked} / {totalInstances}</Box>
          </SpaceBetween>
        </ColumnLayout>
      </Container>

      {/* Post-deploy result banner */}
      {postDeployResult && (
        <Alert type={postDeployResult.status === 'completed' ? 'success' : 'error'} dismissible onDismiss={() => setPostDeployResult(null)}>
          <SpaceBetween size="xs">
            <StatusIndicator type={postDeployResult.status === 'completed' ? 'success' : 'error'}>
              Post-deploy: {postDeployResult.status}
            </StatusIndicator>
            {Object.entries(postDeployResult.results).map(([k, v]) => (
              <Box key={k} variant="p" fontSize="body-s"><code>{k}</code>: {v}</Box>
            ))}
            {postDeployResult.error && <Box variant="p" color="text-status-error">{postDeployResult.error}</Box>}
          </SpaceBetween>
        </Alert>
      )}

      {/* Main table */}
      <Table
        columnDefinitions={columns}
        items={instances}
        loading={loading}
        loadingText="Loading PC3 instances..."
        trackBy={instanceKey}
        variant="container"
        stripedRows
        stickyHeader
        onRowClick={({ detail }) => setSelectedInstance(
          selectedInstance && instanceKey(selectedInstance) === instanceKey(detail.item) ? null : detail.item
        )}
        empty={
          <Box textAlign="center" padding="xxl" color="text-status-inactive">
            No PC3 instances deployed yet. Deploy from Product Deployments.
          </Box>
        }
        header={<Header counter={`(${totalInstances})`}>Deployed Instances</Header>}
      />

      {/* Detail panel — shows when an instance is selected */}
      {selectedInstance && (
        <Container header={<Header variant="h2"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {(selectedInstance.customDomain || selectedInstance.frontendUrl) && (
                <Button iconName="external" href={selectedInstance.customDomain || selectedInstance.frontendUrl} target="_blank">Open Site</Button>
              )}
              <Button loading={runningPostDeploy === selectedInstance.tenantId} onClick={() => handleRunPostDeploy(selectedInstance)}>
                {selectedInstance.postDeployStatus === 'completed' ? 'Re-run All Steps' : 'Run Post-Deploy'}
              </Button>
            </SpaceBetween>
          }
        >{selectedInstance.tenantName} — {selectedInstance.companyName}</Header>}>
          <SpaceBetween size="l">
            <ColumnLayout columns={3} variant="text-grid">
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">Custom Domain</Box>
                {selectedInstance.customDomain ? (
                  <Link external href={selectedInstance.customDomain}>{selectedInstance.customDomain}</Link>
                ) : <Box color="text-status-inactive">Not configured</Box>}
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">CloudFront</Box>
                {selectedInstance.frontendUrl ? (
                  <Link external href={selectedInstance.frontendUrl} fontSize="body-s">{selectedInstance.frontendUrl.replace('https://', '')}</Link>
                ) : <Box color="text-status-inactive">—</Box>}
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">API</Box>
                <code style={{ fontSize: 12 }}>{selectedInstance.apiUrl || '—'}</code>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">MCP Endpoint</Box>
                <code style={{ fontSize: 12 }}>{selectedInstance.mcpEndpoint || '—'}</code>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">Cognito Pool</Box>
                <code style={{ fontSize: 12 }}>{selectedInstance.cognitoPoolId || '—'}</code>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">CloudFront ID</Box>
                <code style={{ fontSize: 12 }}>{selectedInstance.cloudFrontId || '—'}</code>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">Stack</Box>
                <code style={{ fontSize: 12 }}>{selectedInstance.stackName || '—'}</code>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">Account</Box>
                <code style={{ fontSize: 12 }}>{selectedInstance.customerAccountId || '—'}</code>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">Version</Box>
                <Box>{selectedInstance.version || '—'}</Box>
              </SpaceBetween>
            </ColumnLayout>

            {/* Post-deploy steps with retry */}
            <Container header={<Header variant="h3">Post-Deploy Steps</Header>}>
              <Table
                columnDefinitions={[
                  { id: 'step', header: 'Step', cell: (item: { key: string; label: string; status: string }) => item.label, width: 180 },
                  { id: 'status', header: 'Status', cell: (item: { key: string; label: string; status: string }) => {
                    const si = stepIcon(item.status);
                    return <StatusIndicator type={si.type}>{si.label}</StatusIndicator>;
                  }, width: 280 },
                  { id: 'action', header: '', cell: (item: { key: string; label: string; status: string }) => (
                    <Button variant="inline-link" loading={runningPostDeploy === selectedInstance.tenantId}
                      onClick={() => handleRunStep(selectedInstance, item.key)}>
                      {item.status.startsWith('completed') ? 'Re-run' : 'Run'}
                    </Button>
                  ), width: 80 },
                ]}
                items={STEP_ORDER.map((key) => ({
                  key,
                  label: STEP_LABELS[key] || key,
                  status: (selectedInstance.postDeploySteps || {})[key] || 'pending',
                }))}
                variant="embedded"
                stripedRows
              />
            </Container>
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
}
